import { useEffect, useState, useMemo, useCallback } from "react";
import { Link, useNavigate } from "react-router-dom";
import { MapContainer, TileLayer, Marker, Popup, Circle, useMap } from "react-leaflet";
import L from "leaflet";
import "leaflet/dist/leaflet.css";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Slider } from "@/components/ui/slider";
import { MapPin, List } from "lucide-react";
import usePullToRefresh from "@/hooks/usePullToRefresh";
import PullIndicator from "@/components/PullIndicator";

// Fix default icon paths (Leaflet + Webpack)
delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: "https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon-2x.png",
  iconUrl: "https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon.png",
  shadowUrl: "https://unpkg.com/leaflet@1.9.4/dist/images/marker-shadow.png",
});

const orangePin = L.divIcon({
  html: `<div style="background:#D95338;width:32px;height:32px;border-radius:50% 50% 50% 0;transform:rotate(-45deg);border:3px solid white;box-shadow:0 3px 8px rgba(0,0,0,.3);display:grid;place-items:center;"><div style="transform:rotate(45deg);color:#fbbf24;font-weight:800;font-size:11px">$</div></div>`,
  className: "",
  iconSize: [32, 32],
  iconAnchor: [16, 32],
});

function haversine(a, b) {
  const R = 3958.8; // miles
  const toRad = (v) => (v * Math.PI) / 180;
  const dLat = toRad(b.lat - a.lat);
  const dLng = toRad(b.lng - a.lng);
  const s = Math.sin(dLat / 2) ** 2 + Math.cos(toRad(a.lat)) * Math.cos(toRad(b.lat)) * Math.sin(dLng / 2) ** 2;
  return 2 * R * Math.asin(Math.sqrt(s));
}

function Recenter({ center }) {
  const map = useMap();
  useEffect(() => { if (center) map.setView([center.lat, center.lng], 12); }, [center, map]);
  return null;
}

export default function Map() {
  const navigate = useNavigate();
  const [tasks, setTasks] = useState([]);
  const [me, setMe] = useState(null);
  const [radius, setRadius] = useState([25]); // miles; 0 = off

  const load = useCallback(() => api.get("/tasks").then(({ data }) => setTasks(data.filter(t => t.latitude && t.longitude))), []);

  useEffect(() => {
    load();
    if (navigator.geolocation) {
      navigator.geolocation.getCurrentPosition(
        (pos) => setMe({ lat: pos.coords.latitude, lng: pos.coords.longitude }),
        () => {},
      );
    }
  }, [load]);

  const { pullY, progress, isRefreshing } = usePullToRefresh(load);

  const enriched = useMemo(() => {
    if (!me) return tasks;
    const list = tasks.map(t => ({ ...t, distance: haversine(me, { lat: t.latitude, lng: t.longitude }) }))
      .sort((a, b) => a.distance - b.distance);
    if (!radius[0] || radius[0] >= 100) return list;
    return list.filter((t) => t.distance <= radius[0]);
  }, [tasks, me, radius]);

  const center = me || (tasks[0] ? { lat: tasks[0].latitude, lng: tasks[0].longitude } : { lat: 46.7324, lng: -116.9997 });
  const radiusMeters = radius[0] && radius[0] < 100 ? radius[0] * 1609.34 : null;

  return (
    <div className="space-y-4">
      <PullIndicator pullY={pullY} progress={progress} isRefreshing={isRefreshing} />
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="font-heading text-3xl sm:text-4xl font-extrabold tracking-tight">Tasks on the map</h1>
          <p className="text-slate-600 mt-1 text-sm">{me ? `Showing ${enriched.length} of ${tasks.length} tasks near you.` : "Enable location for distance-based sorting."}</p>
        </div>
        <Button onClick={() => navigate("/feed")} variant="outline" className="rounded-full" data-testid="switch-to-list-btn">
          <List size={14} className="mr-1" /> List view
        </Button>
      </div>

      {me && (
        <div className="bg-white rounded-2xl border border-slate-200/80 p-4 flex items-center gap-4">
          <div className="text-xs font-bold uppercase tracking-wider text-slate-500 whitespace-nowrap">Radius</div>
          <Slider value={radius} onValueChange={setRadius} min={1} max={100} step={1} className="flex-1" data-testid="map-radius-slider" />
          <div className="font-mono-money text-sm text-slate-900 w-20 text-right">
            {radius[0] >= 100 ? "Any" : `${radius[0]} mi`}
          </div>
        </div>
      )}

      <div className="rounded-2xl overflow-hidden border border-slate-200/80 h-[420px] sm:h-[520px]">
        <MapContainer center={[center.lat, center.lng]} zoom={me ? 11 : 4} style={{ height: "100%", width: "100%" }} data-testid="map-container">
          <Recenter center={me} />
          <TileLayer
            attribution='&copy; OpenStreetMap'
            url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
          />
          {me && radiusMeters && (
            <Circle center={[me.lat, me.lng]} radius={radiusMeters} pathOptions={{ color: "#D95338", fillColor: "#D95338", fillOpacity: 0.08, weight: 2 }} />
          )}
          {enriched.map((t) => (
            <Marker key={t.task_id} position={[t.latitude, t.longitude]} icon={orangePin}>
              <Popup>
                <div className="min-w-[180px]">
                  <div className="font-bold text-slate-900">{t.title}</div>
                  <div className="text-xs text-slate-500 mt-1">{t.location}</div>
                  <div className="flex items-center justify-between mt-2">
                    <span className="font-mono-money text-slate-900">${t.budget}</span>
                    {t.distance != null && <span className="text-xs text-slate-500">{t.distance.toFixed(1)} mi</span>}
                  </div>
                  <Link to={`/tasks/${t.task_id}`} className="mt-2 block text-center bg-[#3E2A4E] text-white text-xs font-bold py-2 rounded-full hover:bg-[#2E1F3B]">
                    View task
                  </Link>
                </div>
              </Popup>
            </Marker>
          ))}
        </MapContainer>
      </div>

      {enriched.length > 0 && (
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
          {enriched.slice(0, 6).map((t) => (
            <Link key={t.task_id} to={`/tasks/${t.task_id}`} data-testid={`map-task-${t.task_id}`} className="bg-white border border-slate-200/80 rounded-2xl p-4 card-hover">
              <div className="flex items-start justify-between gap-2">
                <div className="font-heading font-bold text-slate-900 line-clamp-1">{t.title}</div>
                <span className="font-mono-money text-xs px-2 py-0.5 rounded-lg bg-[#3E2A4E] text-[#D9FF3D]">${t.budget}</span>
              </div>
              <div className="text-xs text-slate-500 mt-2 flex items-center justify-between">
                <span className="inline-flex items-center gap-1"><MapPin size={11} /> {t.location}</span>
                {t.distance != null && <span>{t.distance.toFixed(1)} mi</span>}
              </div>
            </Link>
          ))}
        </div>
      )}
    </div>
  );
}
